import { useState } from 'react';
import { REDESIGN_CUTOVER_AT } from '@porter/shared-theme';
import useUser from '../global/useUser';
import { LoadedUser } from '../../interfaces';
import useBusinessWebRedesign from './useBusinessWebRedesign';
import useBusinessWebRedesignToggle from './useBusinessWebRedesignToggle';
import { isReactNativeWebView } from '../../utils/global/helper';

// Keyed on the state so the banner returns when the shop is moved on or off.
function dismissKey(businessId: string, enabled: boolean) {
  return `redesign-banner-dismissed:${businessId}:${enabled ? 'on' : 'off'}`;
}

function readDismissed(key: string) {
  try {
    return localStorage.getItem(key) === '1';
  } catch {
    return false;
  }
}

function useBusinessWebRedesignBanner() {
  const { user } = useUser<LoadedUser>();
  const { eligible, enabled, locked } = useBusinessWebRedesign();
  const { canManage } = useBusinessWebRedesignToggle();
  const [hidden, setHidden] = useState(false);

  const businessId = user?.business?.id;
  const key = businessId ? dismissKey(businessId, enabled) : null;

  const isBannerShown =
    Date.now() < REDESIGN_CUTOVER_AT.getTime() &&
    canManage &&
    eligible &&
    !locked &&
    !!key &&
    !hidden &&
    !readDismissed(key) &&
    !isReactNativeWebView();

  const hideBanner = () => {
    setHidden(true);
    if (!key) return;
    try {
      localStorage.setItem(key, '1');
    } catch {
      // Storage unavailable; the banner simply returns next load.
    }
  };

  return { isBannerShown, enabled, hideBanner };
}

export default useBusinessWebRedesignBanner;
